"use client"

import { useEffect, type MouseEvent, type ReactNode } from "react"
import Link from "next/link"
import { analytics, trackEvent, type EventProperties } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { appStoreCampaignToken } from "@/lib/app-store-campaign"

const APP_PAGE = "/app"

function emitCtaSurfaceShown(ctaLocation: string, props?: EventProperties) {
  analytics.ctaSurfaceShown?.(ctaLocation, props)
}

function getReferrerDomain(): string | undefined {
  if (typeof document === "undefined" || !document.referrer) return undefined
  try {
    return new URL(document.referrer).hostname
  } catch {
    return undefined
  }
}

function getAttribution() {
  if (typeof window === "undefined") {
    return { source: undefined, referrerDomain: undefined }
  }

  const params = new URLSearchParams(window.location.search)
  const source =
    params.get("from") ??
    params.get("utm_source") ??
    params.get("ref") ??
    undefined

  return {
    source,
    referrerDomain: getReferrerDomain(),
  }
}

interface AppLandingAnalyticsProps {
  hasAppStoreUrl: boolean
}

export function AppLandingAnalytics({ hasAppStoreUrl }: AppLandingAnalyticsProps) {
  useEffect(() => {
    const { source, referrerDomain } = getAttribution()
    analytics.appLandingViewed({
      page: APP_PAGE,
      source,
      referrerDomain,
      hasAppStoreUrl,
    })

    if (source === "smart_banner" || referrerDomain === "apps.apple.com") {
      analytics.appBannerClick(APP_PAGE, { source, referrerDomain })
    }
  }, [hasAppStoreUrl])

  return null
}

interface AppStoreTrackedLinkProps {
  href: string
  appId?: string
  placement: "hero" | "lower" | "pricing" | "final"
  className?: string
  children: ReactNode
}

export function AppStoreTrackedLink({
  href,
  appId,
  placement,
  className,
  children,
}: AppStoreTrackedLinkProps) {
  const ctaLocation = `app_landing_${placement}`
  const campaignToken = appStoreCampaignToken(ctaLocation)
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    ctaLocation,
    {
      page: APP_PAGE,
      placement,
      destination: "app_store",
      destination_url: href,
      campaign_token: campaignToken,
      surface: "app_landing_store_badge",
    },
    { emit: emitCtaSurfaceShown },
  )

  function handleClick() {
    const { source, referrerDomain } = getAttribution()
    analytics.appStoreCtaClicked({
      page: APP_PAGE,
      placement,
      destinationUrl: href,
      appId,
      source,
      referrerDomain,
    })
    trackEvent("cta_clicked", {
      page: APP_PAGE,
      cta_location: ctaLocation,
      cta_text: "View in App Store",
      destination: "app_store",
      destination_url: href,
      campaign_token: campaignToken,
      placement,
      source,
    })
  }

  return (
    <Link
      ref={impressionRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={handleClick}
    >
      {children}
    </Link>
  )
}

interface AppLandingTrackedLinkProps {
  href: string
  ctaText: string
  placement: string
  className?: string
  children: ReactNode
}

export function AppLandingTrackedLink({
  href,
  ctaText,
  placement,
  className,
  children,
}: AppLandingTrackedLinkProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    const { source } = getAttribution()
    trackEvent("cta_clicked", {
      page: APP_PAGE,
      cta_location: `app_landing_${placement}`,
      cta_text: ctaText,
      destination: href,
      source,
    })

    if (!href.startsWith("#")) return
    event.preventDefault()
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <Link href={href} className={className} onClick={handleClick}>
      {children}
    </Link>
  )
}
